import axios from 'axios';
import { Job, JobStats, ApiResponse, PaginatedResponse, User, UserStats, Bookmark, AuthenticatedUserStats, AccountInfoStats, Transaction, TokenPurchaseCostDto, OrderDto, UserCreditSettingsDto } from '../types';
import { mockApi } from './mockData';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const API_KEY = 'YOUR_SECURE_API_KEY';//process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    ...(API_KEY ? { 'X-API-KEY': API_KEY } : {}),
},
});

// Add request interceptor for authentication
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('jwt');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle JWT expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // JWT token has expired or is invalid
      localStorage.removeItem('jwt');
      localStorage.removeItem('authUser');
      
      // Redirect to login page
      window.location.href = '/loginPage';
    }
    return Promise.reject(error);
  }
);

const useMockApi = process.env.NODE_ENV === 'development' && !process.env.REACT_APP_USE_REAL_API;

export const publicApi = {
  requestAccountDeletion: async (payload: { email: string; captchaAnswer: string }) => {
    // Public endpoint to initiate an account deletion request email.
    // Backend should:
    // - validate the email and CAPTCHA
    // - generate a time-limited token
    // - send a confirmation email with a secure link
    const response = await api.post('/public/account/account-deletion-request', payload);
    return response.data;
  },

  confirmAccountDeletion: async (payload: { token: string; code: string }) => {
    // Public endpoint to confirm account deletion with token and code.
    // Backend should:
    // - validate the token and code
    // - verify the token hasn't expired
    // - mark the account for deletion
    const response = await api.post('/public/account/confirm-deletion', payload);
    return response.data;
  },
};

export const jobsApi = {
  getJobs: async (page = 1, limit = 10, status?: string, search?: string, userId?: string) => {
    // if (useMockApi) {
    //   return mockApi.getJobs(page, limit, status, search, userId);
    // }
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    if (userId) params.append('userId', userId);
    const response = await api.get<PaginatedResponse<Job>>(`/transcribe/jobs?${params}`);
    return response.data;
  },

  getUserJobs: async (page = 1, limit = 10, status?: string, search?: string) => {
    // if (useMockApi) {
    //   return mockApi.getJobs(page, limit, status, search, userId);
    // }
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (status) params.append('status', status);
    if (search) params.append('search', search);
    const response = await api.get<PaginatedResponse<Job>>(`/users/jobs?${params}`);
    return response.data;
  },

  uploadAudio: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('jwt');
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const url = baseURL.replace(/\/$/, '') + '/transcribe';
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        // Do not set Content-Type so browser sets multipart/form-data with boundary
      },
      body: formData,
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Upload failed: ${response.status}`);
    }
    return response.text();
  },

  getJob: async (id: string) => {
    if (useMockApi) {
      return mockApi.getJob(id);
    }
    const response = await api.get<ApiResponse<Job>>(`/jobs/${id}`);
    return response.data;
  },

  getStats: async (userId?: string) => {
    if (useMockApi) {
      return mockApi.getStats(userId);
    }
    const params = userId ? `?userId=${userId}` : '';
    const response = await api.get<ApiResponse<JobStats>>(`/jobs/stats${params}`);
    return response.data;
  },

  updateJob: async (id: string, updates: Partial<Job>) => {
    if (useMockApi) {
      return mockApi.updateJob(id, updates);
    }
    const response = await api.patch<ApiResponse<Job>>(`/jobs/${id}`, updates);
    return response.data;
  },

  deleteJob: async (id: string) => {
    if (useMockApi) {
      return mockApi.deleteJob(id);
    }
    const response = await api.delete<ApiResponse<void>>(`/jobs/${id}`);
    return response.data;
  },

  retryTranscription: async (id: string) => {
    if (useMockApi) {
      return mockApi.retryTranscription(id);
    }
    const response = await api.post<ApiResponse<Job>>(`/jobs/${id}/retry`);
    return response.data;
  },

  downloadTranscription: async (id: string) => {
    if (useMockApi) {
      return mockApi.downloadTranscription(id);
    }
    const response = await api.get<string>(`/transcribe/${id}/text`);
    const blob = new Blob([response.data], { type: 'text/plain;charset=utf-8' });
    return blob;
  },

  getBookmarks: async (jobId: string) => {
    //if (useMockApi) {
    //  return mockApi.getBookmarks(jobId);
    //}
    const response = await api.get<Bookmark[]>(`/users/${jobId}/bookmarks`);
    return response.data;
  },
};

export const transactionsApi = {
  getTransactions: async (page = 1, limit = 10, type?: string): Promise<ApiResponse<PaginatedResponse<Transaction>>> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (type) params.append('type', type);
    const response = await api.get<ApiResponse<PaginatedResponse<Transaction>>>(`/credits/transactions?${params}`);
    console.log("Raw response:", response);
    return response.data;
  },
};

export const usersApi = {
  getUsers: async () => {
    //if (useMockApi) {
    //  return mockApi.getUsers();
    //}
    const response = await api.get<ApiResponse<User[]>>('/users');
    return response.data;
  },

  getUser: async (id: string) => {
    if (useMockApi) {
      return mockApi.getUser(id);
    }
    const response = await api.get<ApiResponse<User>>(`/users/${id}`);
    return response.data;
  },

  getUserStats: async (userId: string) => {
    if (useMockApi) {
      return mockApi.getUserStats(userId);
    }
    const response = await api.get<ApiResponse<UserStats>>(`/users/${userId}/stats`);
    return response.data;
  },

  getUserAccountInfo: async () => {
    const response = await api.get<ApiResponse<AccountInfoStats>>(`/users/account-info`);
    return response.data;
  },

  getAuthenticatedUserStats: async () => {
    const response = await api.get<ApiResponse<AuthenticatedUserStats>>(`/users/stats`);
    return response.data;
  },

  getTokenCost: async () => {
    const response = await api.get<ApiResponse<TokenPurchaseCostDto>>('/credits/tokenCost');
    return response.data;
  },
};

export const creditsApi = {
  getCreditSettings: async () => {
    const response = await api.get<ApiResponse<UserCreditSettingsDto>>('/credits/settings');
    return response.data;
  },
};

export const paymentsApi = {
  createCheckoutSession: async (creditsAmount: number) => {
    const response = await api.post<ApiResponse<{ url: string; orderId: string; orderNumber: string }>>('/payments/create-checkout-session', {
      creditsAmount: creditsAmount
    });
    return response.data;
  },

  getOrders: async (page = 0, size = 20, status?: string): Promise<ApiResponse<PaginatedResponse<OrderDto>>> => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('size', size.toString());
    if (status) params.append('status', status);
    const response = await api.get<ApiResponse<PaginatedResponse<OrderDto>>>(`/payments/orders?${params}`);
    return response.data;
  },

  cancelOrder: async (sessionId: string) => {
    const response = await api.post<ApiResponse<String>>(`/payments/cancel-order`,{sessionId: sessionId});
    return response.data;
  },
};

export interface TermsCurrentResponse {
  currentVersion: string;
  userHasAccepted: boolean;
  termsText: string;
}

export interface TermsStatusResponse {
  email: string;
  termsVersion: string;
  accepted: boolean;
  acceptedAt?: string;
}

export interface TermsAcceptResponse {
  acceptance: {
    email: string;
    termsVersion: string;
    accepted: boolean;
    acceptedAt: string;
  };
  token: string;
}

export const termsApi = {
  getCurrentTerms: async (): Promise<ApiResponse<TermsCurrentResponse>> => {
    // This endpoint can be called without authentication (public)
    const response = await api.get<ApiResponse<TermsCurrentResponse>>('/terms/current');
    return response.data;
  },

  getTermsStatus: async (): Promise<ApiResponse<TermsStatusResponse>> => {
    // This endpoint requires authentication
    const response = await api.get<ApiResponse<TermsStatusResponse>>('/terms/status');
    return response.data;
  },

  acceptTerms: async (termsVersion?: string, termsHash?: string): Promise<ApiResponse<TermsAcceptResponse>> => {
    // This endpoint requires authentication and returns a new token
    const response = await api.post<ApiResponse<TermsAcceptResponse>>('/terms/accept', {
      termsVersion,
      termsHash,
    });
    return response.data;
  },
};

export default api; 